// =============================================================================
// Module Project - Hook: useNotificationsStore
// Manage in-app notifications
// IMPORTANT: Ce store n'importe PAS supabase directement
// =============================================================================

import { useState, useCallback, useEffect } from 'react';
import {
  fetchNotifications,
  fetchProjectNotifications,
  countUnreadNotifications,
  createNotification,
  createNotificationsForRecipients,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  formatNotificationMessage,
  type ProjectNotification,
  type NotificationType,
  type EntityType,
  type NotificationPayload,
} from '../data/notificationRepository';
import { subscribeToNotifications } from '../data/notificationSubscriptionService';
import { getWatcherMemberIds } from '../data/watcherRepository';

export function useNotificationsStore(orgId: string | undefined, projectId?: string) {
  const [notifications, setNotifications] = useState<ProjectNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // LOAD NOTIFICATIONS
  // ─────────────────────────────────────────────────────────────────────────
  const loadNotifications = useCallback(async (limit: number = 50) => {
    setLoading(true);
    
    const data = projectId
      ? await fetchProjectNotifications(projectId, limit)
      : await fetchNotifications(limit);
    
    setNotifications(data);
    setUnreadCount(data.filter(n => !n.isRead).length);
    setLoading(false);
  }, [projectId]);

  // ─────────────────────────────────────────────────────────────────────────
  // REFRESH UNREAD COUNT
  // ─────────────────────────────────────────────────────────────────────────
  const refreshUnreadCount = useCallback(async () => {
    const count = await countUnreadNotifications();
    setUnreadCount(count);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // MARK AS READ (optimistic)
  // ─────────────────────────────────────────────────────────────────────────
  const markNotificationAsRead = useCallback(async (notificationId: string): Promise<boolean> => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    const success = await markAsRead(notificationId);
    
    if (!success) {
      // Rollback
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, isRead: false } : n)
      );
      setUnreadCount(prev => prev + 1);
    }

    return success;
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // MARK ALL AS READ
  // ─────────────────────────────────────────────────────────────────────────
  const markAllNotificationsAsRead = useCallback(async (): Promise<boolean> => {
    // Optimistic update
    const previousNotifications = [...notifications];
    const previousCount = unreadCount;
    
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);

    const success = await markAllAsRead(projectId);
    
    if (!success) {
      // Rollback
      setNotifications(previousNotifications);
      setUnreadCount(previousCount);
    }

    return success;
  }, [notifications, unreadCount, projectId]);

  // ─────────────────────────────────────────────────────────────────────────
  // DELETE NOTIFICATION
  // ─────────────────────────────────────────────────────────────────────────
  const removeNotification = useCallback(async (notificationId: string): Promise<boolean> => {
    const notification = notifications.find(n => n.id === notificationId);
    
    // Optimistic remove
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (notification && !notification.isRead) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    const success = await deleteNotification(notificationId);
    
    if (!success && notification) {
      // Rollback
      setNotifications(prev => [...prev, notification]);
      if (!notification.isRead) {
        setUnreadCount(prev => prev + 1);
      }
    }

    return success;
  }, [notifications]);

  // ─────────────────────────────────────────────────────────────────────────
  // NOTIFY WATCHERS (helper for other stores to use)
  // ─────────────────────────────────────────────────────────────────────────
  const notifyWatchers = useCallback(async (
    taskId: string,
    pId: string,
    type: NotificationType,
    entityType: EntityType,
    entityId: string,
    payload: NotificationPayload,
    actorMemberId?: string
  ): Promise<void> => {
    if (!orgId) return;

    const watcherIds = await getWatcherMemberIds(taskId);
    
    await createNotificationsForRecipients(
      orgId,
      pId,
      watcherIds,
      type,
      entityType,
      entityId,
      payload,
      actorMemberId
    );
  }, [orgId]);

  // ─────────────────────────────────────────────────────────────────────────
  // NOTIFY SPECIFIC MEMBER
  // ─────────────────────────────────────────────────────────────────────────
  const notifyMember = useCallback(async (
    pId: string,
    recipientMemberId: string,
    type: NotificationType,
    entityType: EntityType,
    entityId: string,
    payload: NotificationPayload
  ): Promise<void> => {
    if (!orgId) return;

    await createNotification(
      orgId,
      pId,
      recipientMemberId,
      type,
      entityType,
      entityId,
      payload
    );
  }, [orgId]);

  // ─────────────────────────────────────────────────────────────────────────
  // REALTIME SUBSCRIPTION (via service - no direct supabase import)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!orgId) return;

    const handleNewNotification = (notification: ProjectNotification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    };

    const unsubscribe = subscribeToNotifications(
      { orgId, projectId },
      handleNewNotification
    );

    return unsubscribe;
  }, [orgId, projectId]);

  // ─────────────────────────────────────────────────────────────────────────
  // CLEAR
  // ─────────────────────────────────────────────────────────────────────────
  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    loadNotifications,
    refreshUnreadCount,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    removeNotification,
    notifyWatchers,
    notifyMember,
    formatMessage: formatNotificationMessage,
    clearNotifications,
  };
}
